/**
 * @module AdapterDetailPage
 * @package @geenius/adapters/solidjs-ark
 * @description Ark-native SolidJS adapter detail page for a single provider
 * domain.
 */

import { Progress } from "@ark-ui/solid/progress";
import type { AdapterDomain } from "@geenius/adapters";
import {
  DOMAIN_DESCRIPTIONS,
  DOMAIN_ICONS,
  DOMAIN_LABELS,
  getProvidersForDomain,
} from "@geenius/adapters";
import { createAdapterStatus } from "@geenius/adapters/solidjs";
import type { JSX } from "solid-js";
import { createSignal, For, Show } from "solid-js";
import { AdapterConfigForm } from "../components/AdapterConfigForm";
import { AdapterStatusBadge } from "../components/AdapterStatusBadge";
import { classNames } from "../lib/classNames";

/**
 * Props for rendering the Ark Solid single-domain adapter detail page.
 *
 * @property domain Adapter domain whose provider details should be displayed.
 * @property onBack Optional callback for returning to the dashboard view.
 * @property class Optional class name merged into the page container.
 */
export interface AdapterDetailPageProps {
  domain: AdapterDomain;
  onBack?: () => void;
  class?: string;
}

/** Renders the Ark Solid adapter detail page for one adapter domain. */
export function AdapterDetailPage(props: AdapterDetailPageProps): JSX.Element {
  const status = createAdapterStatus(props.domain);
  const providers = getProvidersForDomain(props.domain);
  const [isEditing, setIsEditing] = createSignal(false);
  const latency = () => status().latency ?? 0;
  const latencyPercent = () => Math.min(100, (latency() / 1000) * 100);

  return (
    <div
      class={classNames(
        "mx-auto max-w-3xl space-y-8 text-slate-950",
        props.class,
      )}
      data-gn-adapters-ark-page="adapter-detail"
    >
      <Show when={props.onBack !== undefined}>
        <button
          class="inline-flex items-center gap-1.5 text-sm text-slate-500 transition-colors hover:text-slate-950"
          onClick={props.onBack}
          type="button"
        >
          Back to Adapters
        </button>
      </Show>

      <div class="flex items-start gap-5">
        <div
          aria-hidden="true"
          class="flex h-16 w-16 items-center justify-center rounded-md border border-slate-200 bg-white text-3xl shadow-sm"
        >
          {DOMAIN_ICONS[props.domain]}
        </div>
        <div class="flex-1">
          <div class="flex flex-wrap items-center gap-3">
            <h1 class="text-2xl font-extrabold text-slate-950">
              {DOMAIN_LABELS[props.domain]}
            </h1>
            <AdapterStatusBadge status={status().status} />
          </div>
          <p class="mt-1 text-sm text-slate-500">
            {DOMAIN_DESCRIPTIONS[props.domain]}
          </p>
        </div>
      </div>

      <div class="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
        <h2 class="mb-4 text-xs font-semibold uppercase text-slate-500">
          Current Status
        </h2>
        <div class="grid gap-4 sm:grid-cols-3">
          <div>
            <p class="text-[0.625rem] uppercase text-slate-500">Provider</p>
            <p class="mt-0.5 text-sm font-semibold text-slate-950">
              {status().provider !== "none" ? status().provider : "None"}
            </p>
          </div>
          <div>
            <p class="text-[0.625rem] uppercase text-slate-500">Status</p>
            <p class="mt-0.5">
              <AdapterStatusBadge status={status().status} />
            </p>
          </div>
          <div>
            <p class="text-[0.625rem] uppercase text-slate-500">Last Checked</p>
            <p class="mt-0.5 text-sm text-slate-500">
              <Show fallback={"Not checked"} when={status().lastCheckedAt}>
                {(lastCheckedAt) =>
                  new Date(lastCheckedAt()).toLocaleTimeString()
                }
              </Show>
            </p>
          </div>
        </div>

        <Show when={status().error}>
          <div class="mt-4 rounded-md border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-950">
            <strong>Error:</strong> {status().error}
          </div>
        </Show>

        <Show when={status().latency !== undefined}>
          <Progress.Root class="mt-4" max={100} value={latencyPercent()}>
            <div class="mb-1 flex items-center justify-between gap-3">
              <Progress.Label class="text-[0.625rem] uppercase text-slate-500">
                Latency
              </Progress.Label>
              <span class="text-xs text-slate-500">{latency()}ms</span>
            </div>
            <Progress.Track
              aria-label={`${DOMAIN_LABELS[props.domain]} latency`}
              class="h-2 w-full overflow-hidden rounded-full bg-slate-100"
            >
              <Progress.Range
                class={classNames(
                  "h-full rounded-full transition-all",
                  latency() < 100
                    ? "bg-emerald-500"
                    : latency() < 500
                      ? "bg-amber-500"
                      : "bg-red-500",
                )}
              />
            </Progress.Track>
          </Progress.Root>
        </Show>
      </div>

      <div class="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-xs font-semibold uppercase text-slate-500">
            Available Providers
          </h2>
          <span class="text-xs text-slate-500">
            {providers.length} providers
          </span>
        </div>
        <div class="divide-y divide-slate-200">
          <For each={providers}>
            {(provider) => (
              <div class="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
                <div>
                  <p class="text-sm font-semibold text-slate-950">
                    {provider.name}
                  </p>
                  <p class="text-xs text-slate-500">{provider.description}</p>
                </div>
                <span
                  class={classNames(
                    "self-start rounded px-2 py-0.5 text-[0.625rem] font-bold uppercase",
                    provider.tier === "pronto"
                      ? "bg-emerald-100 text-emerald-900"
                      : provider.tier === "lancio"
                        ? "bg-amber-100 text-amber-950"
                        : "bg-slate-100 text-slate-600",
                  )}
                >
                  {provider.tier}
                </span>
              </div>
            )}
          </For>
        </div>
      </div>

      <Show
        fallback={
          <button
            class="w-full rounded-md border border-dashed border-slate-200 bg-white/60 p-6 text-center text-sm text-slate-500 transition-colors hover:border-slate-400 hover:text-slate-950"
            onClick={() => setIsEditing(true)}
            type="button"
          >
            Configure {DOMAIN_LABELS[props.domain]} Adapter
          </button>
        }
        when={isEditing()}
      >
        <AdapterConfigForm
          domain={props.domain}
          onCancel={() => setIsEditing(false)}
          onSave={async () => {
            setIsEditing(false);
          }}
        />
      </Show>
    </div>
  );
}
